import { SG_EVENT_NAMES } from '@/utils/tools/segment/eventNames';
import { trackEvent } from '@/utils/tools/segment/tracking';
import { Alert, Avatar, Button, Dropdown, Flex, Form, Input, Modal, Select, Space, Switch, Table, Typography } from 'antd';
import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { LibraryContext } from './context-provider';
const { Text, Link } = Typography;

export default function ShareModal({ open, setOpen }) {
   const [shareData, setShareData] = useState([]);
   const [publicShare, setPublicShare] = useState(false);
   const [publicShareLoading, setPublicShareLoading] = useState(false);
   const [alertVisible, setAlertVisible] = useState(false);
   const [alert, setAlert] = useState(null);

   const permissionOptions = [
      { label: 'Can view', value: 'read', key: 'read' },
      { label: 'Can edit', value: 'write', key: 'write' },
   ];

   // get library context
   const { selectedLibrary, library } = useContext(LibraryContext);

   useEffect(() => {
      if (!selectedLibrary || selectedLibrary.length === 0) return;
      setShareData(selectedLibrary[0]?.sharedWith);
      setPublicShare(selectedLibrary[0]?.public);
   }, [selectedLibrary]);

   // if selectedLibrary is empty, return null
   if (!selectedLibrary || selectedLibrary.length === 0) return null;

   function handleOnCancel() {
      setOpen(false);
   }

   // --- function to handle invite by email ---
   async function inviteByEmail(value) {
      const selected = selectedLibrary[0];

      // check if email is already shared
      const isShared = shareData.find((item) => item.email === value.email);
      if (isShared) {
         setAlert({ type: 'error', message: 'This file is already shared with this user.' });
         setAlertVisible(true);
         return;
      }

      if (selected.type === 'folder') {
         // update folder shared with
         const newSharedWith = [...shareData, { email: value.email, permission: value.permission }];
         const res = await axios.patch('/api/library/folder/' + selected._id, { sharedWith: newSharedWith });
         if (!res.statusText === 'OK') {
            // show error alert
            setAlert({ type: 'error', message: 'Something went wrong. Please try again.' });
            setAlertVisible(true);
            return;
         }

         // show success alert
         setAlert({ type: 'success', message: 'Folder shared successfully.' });
         setAlertVisible(true);
         // hide alert after 3 seconds
         setTimeout(() => {
            setAlertVisible(false);
         }, 3000);

         // update shareData
         setShareData(newSharedWith);
      }
      if (selected.type === 'file') {
         // update file shared with
         const newSharedWith = [...shareData, { email: value.email, permission: value.permission }];
         const res = await axios.patch('/api/library/file/' + selected._id, { sharedWith: newSharedWith });
         if (!res.statusText === 'OK') {
            // show error alert
            setAlert({ type: 'error', message: 'Something went wrong. Please try again.' });
            setAlertVisible(true);
            return;
         }

         // show success alert
         setAlert({ type: 'success', message: 'File shared successfully.' });
         setAlertVisible(true);
         // hide alert after 3 seconds
         setTimeout(() => {
            setAlertVisible(false);
         }, 3000);

         // update shareData
         setShareData(newSharedWith);
      }

      // track event
      trackEvent(SG_EVENT_NAMES.USER_SHARE_LIBRARY, {
         type: selected.type,
      });
   }

   // --- function to handle public share switch ---
   async function handlePublicShareChange() {
      setPublicShareLoading(true);
      setPublicShare(!publicShare);
      // update file public share
      try {
         if (selectedLibrary[0].type === 'folder') {
            const resp = await axios.patch('/api/library/folder/' + selectedLibrary[0]._id, { public: !publicShare });
         }
         if (selectedLibrary[0].type === 'file') {
            const resp = await axios.patch('/api/library/file/' + selectedLibrary[0]._id, { public: !publicShare });
         }

         // show success alert
         setAlert({ type: 'success', message: 'Public share updated successfully.' });
         setAlertVisible(true);
         // hide alert after 3 seconds
         setTimeout(() => {
            setAlertVisible(false);
         }, 3000);
      } catch (error) {
         setAlert({ type: 'error', message: error.response.data.message });
         setAlertVisible(true);
         setTimeout(() => {
            setAlertVisible(false);
         }, 3000);
      }
      // revert public share
      setPublicShare(!publicShare);
      setPublicShareLoading(false);
   }

   // --- function to handle update permission ---
   async function handleUpdatePermission(user, permission) {
      const selected = selectedLibrary[0];
      let newSharedWith = shareData;

      // if permission is remove, remove user from sharedWith
      if (permission === 'remove') {
         newSharedWith = shareData.filter((item) => item.email !== user.email);
      } else {
         newSharedWith = shareData.map((item) => {
            if (item.email === user.email) {
               return { ...item, permission: permission };
            }
            return item;
         });
      }

      try {
         if (selected.type === 'folder') {
            const res = await axios.patch('/api/library/folder/' + selected._id, { sharedWith: newSharedWith });
         }
         if (selected.type === 'file') {
            const res = await axios.patch('/api/library/file/' + selected._id, { sharedWith: newSharedWith });
         }
         if (permission === 'remove') {
            setAlert({ type: 'success', message: 'User removed successfully.' });
         }
         if (permission === 'read') {
            setAlert({ type: 'success', message: 'Permission updated to view only.' });
         }
         if (permission === 'write') {
            setAlert({ type: 'success', message: 'Permission updated to edit.' });
         }
         setAlertVisible(true);
         setTimeout(() => {
            setAlertVisible(false);
         }, 3000);
         setShareData(newSharedWith);
      } catch (error) {
         setAlert({ type: 'error', message: error.response.data.message });
         setAlertVisible(true);
         setTimeout(() => {
            setAlertVisible(false);
         }, 3000);
      }
   }

   const shareColumns = [
      {
         title: 'Avatar',
         dataIndex: 'avatar',
         key: 'avatar',
         render: (text, record) => <Avatar src={`/api/user/public/avatar?email=${record.email}`} />,
      },
      {
         title: 'Email',
         dataIndex: 'email',
         key: 'email',
      },
      {
         title: 'Permission',
         dataIndex: 'permission',
         key: 'permission',
         width: 120,
         render: (text, record) => (
            <>
               {record.permission === 'creator' && <Text type="secondary">Creator</Text>}
               {record.permission !== 'creator' && (
                  <Dropdown
                     menu={{
                        items: permissionOptions.concat({ label: 'Remove', value: 'remove', key: 'remove' }),
                        selectedKeys: [text],
                        selectable: true,
                        onSelect: (item) => {
                           handleUpdatePermission(record, item.key);
                        },
                     }}
                     trigger={['click']}
                  >
                     <Button type="text" size="small" block>
                        <Flex
                           gap={32}
                           align="center"
                           justify="space-between"
                           style={{ width: '100%' }}
                        >
                           {text === 'read' ? 'View only' : text === 'write' ? 'Can edit' : text === 'undifined'}
                           <i className="fi fi-rr-angle-small-down" />
                        </Flex>
                     </Button>
                  </Dropdown>
               )}
            </>
         ),
      },
   ];

   return (
      <>
         <Modal title="Share files" open={open} onCancel={handleOnCancel} footer={null} width={600} destroyOnClose>
            <Flex vertical gap={32}>
               {alertVisible && <Alert type={alert.type} message={alert.message} showIcon />}
               {/* Share by email */}
               <Flex gap={32}>
                  <Form layout="inline" style={{ width: '100%' }} onFinish={inviteByEmail} id="inviteByEmail">
                     <Space.Compact style={{ width: '100%' }} size="large">
                        <Form.Item name="email" noStyle rules={[{ required: true, message: 'Please input email!' }]}>
                           <Input placeholder="User email" size="large" />
                        </Form.Item>
                        <Form.Item name="permission" noStyle initialValue="read">
                           <Select style={{ width: 120 }} options={permissionOptions} />
                        </Form.Item>
                     </Space.Compact>
                  </Form>
                  <Button size="large" type="primary" style={{ width: 110 }} form="inviteByEmail" htmlType="submit">
                     Invite
                  </Button>
               </Flex>

               {/* Share by link */}
               <Flex vertical gap={32}>
                  <Flex gap={32} align="center" justify="space-between">
                     <Text strong>Share by public link</Text>
                     <Switch
                        loading={publicShareLoading}
                        checked={publicShare}
                        onChange={() => handlePublicShareChange()}
                     />
                  </Flex>

                  {publicShare && (
                     <Space direction="vertical" size={4}>
                        <Link type="link" copyable>
                           {selectedLibrary[0].type === 'folder'
                              ? `${window.location.origin}/folder/${selectedLibrary[0]._id}`
                              : selectedLibrary[0].type === 'file'
                              ? `${window.location.origin}/library/public/file/${selectedLibrary[0]._id}`
                              : null}
                        </Link>
                        <Text type="secondary">Your brand is now public, anyone with this link can view it. </Text>
                     </Space>
                  )}
               </Flex>

               {/* Shared with */}
               <Flex vertical gap={32}>
                  <Text strong>Shared with</Text>

                  <Table
                     size="small"
                     showHeader={false}
                     pagination={false}
                     dataSource={shareData}
                     columns={shareColumns}
                     variant="borderless"
                  />
               </Flex>
            </Flex>
         </Modal>
      </>
   );
}
